import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {Page, Pageable} from '../../../model/rest/base';
import {Dates} from '../../utils/calendar-utils';
import {V3Student, V3Teacher, V3TeacherProfile,} from '../../../model/v3/schools';
import {V3Person} from '../../../model/v3/people';
import * as LessonsV3 from '../../../model/v3/lessons';
import {
  appendCollection,
  appendIfDefined,
  createPageableParams,
  map404ToNull,
  serializeDateInput,
} from './rest-v3-utils';

@Injectable({
  providedIn: 'root',
})
export class ManagerColV3RestService {
  constructor(private http: HttpClient) {
  }

  private studentsUrl(schoolId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/schools/${schoolId}/students${path}`;
  }

  private teachersUrl(schoolId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/schools/${schoolId}/teachers${path}`;
  }

  private lessonsUrl(path: string = ''): string {
    return `${environment.eventsApiBase}/v1/lessons${path}`;
  }

  private schoolProductsUrl(schoolId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/schools/${schoolId}/products${path}`;
  }

  private peopleUrl(personId: number, path: string = ''): string {
    return `${environment.eventsApiBase}/v1/people/${personId}${path}`;
  }

  findStudentLessonBundles(
    schoolId: number,
    studentId: number,
    productCode?: string | null,
    available?: boolean | null,
    pageable?: Pageable
  ): Observable<Page<LessonsV3.LessonBundle>> {
    let params = createPageableParams(pageable);
    params = appendIfDefined(params, 'productCode', productCode ?? undefined);
    params = appendIfDefined(params, 'available', available);
    return this.http.get<Page<LessonsV3.LessonBundle>>(
      this.studentsUrl(schoolId, `/${studentId}/bundles`),
      {params}
    ).pipe(
      map((page) => {
        page.content = (page.content ?? []).map((bundle) => ({
          ...bundle,
          date: bundle?.date ? Dates.parse(String(bundle.date)) : bundle?.date,
          expiryDate: bundle?.expiryDate
            ? Dates.parse(String(bundle.expiryDate))
            : bundle?.expiryDate,
        }));
        return page;
      })
    );
  }

  findStudentCommits(
    schoolId: number,
    studentId: number,
    productCode?: string | null,
    courseCode?: string | null,
    commitStatuses?: LessonsV3.LessonCommitStatus[] | null,
    lessonStatuses?: LessonsV3.LessonStatus[] | null,
    pageable?: Pageable
  ): Observable<Page<LessonsV3.LessonCommit>> {
    let params = createPageableParams(pageable);
    params = appendIfDefined(params, 'productCode', productCode ?? undefined);
    params = appendIfDefined(params, 'courseCode', courseCode ?? undefined);
    params = appendCollection(params, 'commitStatuses', commitStatuses ?? undefined);
    params = appendCollection(params, 'lessonStatuses', lessonStatuses ?? undefined);
    return this.http.get<Page<LessonsV3.LessonCommit>>(
      this.studentsUrl(schoolId, `/${studentId}/commits`),
      {params}
    ).pipe(
      map((page) => {
        page.content = (page.content ?? []).map((commit) => ({
          ...commit,
          createDate: commit?.createDate
            ? Dates.parse(String(commit.createDate))
            : commit?.createDate,
          updateDate: commit?.updateDate
            ? Dates.parse(String(commit.updateDate))
            : commit?.updateDate,
        }));
        return page;
      })
    );
  }

  getTeacher(schoolId: number, teacherId: number): Observable<V3Teacher> {
    return this.http.get<V3Teacher>(this.teachersUrl(schoolId, `/${teacherId}`));
  }

  getStudent(schoolId: number, studentId: number): Observable<V3Student> {
    return this.http.get<V3Student>(this.studentsUrl(schoolId, `/${studentId}`));
  }

  private getStudentPerson(schoolId: number, studentId: number): Observable<V3Person> {
    return this.getStudent(schoolId, studentId).pipe(map((student) => student.person));
  }

  getStudentTechnicalProfile(
    schoolId: number,
    studentId: number
  ): Observable<LessonsV3.PersonalSettings> {
    return this.getStudentPerson(schoolId, studentId).pipe(
      switchMap((person) =>
        this.http.get<LessonsV3.PersonalSettings>(this.peopleUrl(person.id, '/col-settings'))
      )
    );
  }

  getTeacherProfile(schoolId: number, teacherId: number): Observable<V3TeacherProfile> {
    return this.http.get<V3TeacherProfile>(this.teachersUrl(schoolId, `/${teacherId}/profile`));
  }

  findLessons(
    type?: string | null,
    lessonType?: LessonsV3.LessonType | null,
    view?: string | null,
    eid?: string | null,
    studentId?: number | null,
    teacherId?: number | null,
    groupId?: number | null,
    groupEid?: string | null,
    productCode?: string | null,
    statuses?: LessonsV3.LessonStatus[] | null,
    dateFrom?: Date | string | null,
    dateTo?: Date | string | null,
    pageable?: Pageable
  ): Observable<Page<LessonsV3.LFViewBase>> {
    let params = createPageableParams(pageable);
    params = appendIfDefined(params, 'type', type ?? undefined);
    params = appendIfDefined(params, 'lessonType', lessonType);
    params = appendIfDefined(params, 'view', view ?? undefined);
    params = appendIfDefined(params, 'eid', eid ?? undefined);
    params = appendIfDefined(params, 'studentId', studentId);
    params = appendIfDefined(params, 'teacherId', teacherId);
    params = appendIfDefined(params, 'groupId', groupId);
    params = appendIfDefined(params, 'groupEid', groupEid ?? undefined);
    params = appendIfDefined(params, 'productCode', productCode ?? undefined);
    params = appendCollection(params, 'statuses', statuses ?? undefined);
    params = appendIfDefined(params, 'dateFrom', serializeDateInput(dateFrom));
    params = appendIfDefined(params, 'dateTo', serializeDateInput(dateTo));
    return this.http.get<Page<LessonsV3.LFViewBase>>(this.lessonsUrl(), {params});
  }

  getLessonStudents(lessonId: number): Observable<LessonsV3.LessonStudentParticipant<LessonsV3.StudentRef>[]> {
    return this.http.get<LessonsV3.LessonStudentParticipant<LessonsV3.StudentRef>[]>(
      this.lessonsUrl(`/${lessonId}/students`)
    );
  }

  getLessonDetails(
    _schoolId: number,
    lessonId: number,
    view: string = 'teacher-full',
    viewStudentId?: number | null
  ): Observable<LessonsV3.LFViewBase> {
    let params = appendIfDefined(new HttpParams(), 'view', view);
    params = appendIfDefined(params, 'viewStudentId', viewStudentId ?? undefined);
    return this.http.get<LessonsV3.LFViewBase>(this.lessonsUrl(`/${lessonId}`), {params}).pipe(
      map((lesson: LessonsV3.LFViewBase & { lessonMetric?: LessonsV3.V3LessonMetric }) => {
        if (!lesson?.lessonMetric) {
          return lesson;
        }
        lesson.lessonMetric.plannedStartDate = lesson.lessonMetric.plannedStartDate
          ? Dates.parse(String(lesson.lessonMetric.plannedStartDate))
          : lesson.lessonMetric.plannedStartDate;
        lesson.lessonMetric.started = lesson.lessonMetric.started
          ? Dates.parse(String(lesson.lessonMetric.started))
          : lesson.lessonMetric.started;
        return lesson;
      })
    );
  }

  getProductContexts(
    schoolId: number,
    studentId: number,
    productCode?: string | null
  ): Observable<LessonsV3.ProductContextDetails[] | null> {
    let params = new HttpParams();
    params = appendIfDefined(params, 'productCode', productCode ?? undefined);
    return this.http
      .get<LessonsV3.ProductContextDetails[]>(
        this.studentsUrl(schoolId, `/${studentId}/product-contexts`),
        {params}
      )
      .pipe(map404ToNull<LessonsV3.ProductContextDetails[]>());
  }

  listProductCourses(schoolId: number, productCode: string): Observable<LessonsV3.Course[]> {
    return this.http.get<LessonsV3.Course[]>(
      this.schoolProductsUrl(schoolId, `/${productCode}/courses`)
    ).pipe(map404ToNull<LessonsV3.Course[]>());
  }

  getCourseContexts(
    schoolId: number,
    studentId: number,
    productCode: string,
    current?: boolean | null
  ): Observable<LessonsV3.ColCourseContext[]> {
    let params = new HttpParams();
    params = appendIfDefined(params, 'current', current ?? undefined);
    return this.http.get<LessonsV3.ColCourseContext[]>(
      this.studentsUrl(schoolId, `/${studentId}/product-contexts/${productCode}/course-contexts`),
      {params}
    ).pipe(map404ToNull<LessonsV3.ColCourseContext[]>());
  }

  updateLessonProgress(
    lessonId: number,
    progress: LessonsV3.LessonProgressDetailsDto
  ): Observable<LessonsV3.LessonProgressDetails> {
    return this.http.put<LessonsV3.LessonProgressDetails>(
      this.lessonsUrl(`/${lessonId}/progress`),
      progress
    );
  }

  /**
   * Single entry point for the lesson /status endpoint.
   * - plain LessonStatus (Booked/Due/InProgress/Complete) → PATCH /status with the status body
   * - LessonStatusAction (cancel/invalidate) → PATCH /status?action=... with the request body
   */
  updateStatus(
    lessonId: number,
    status: LessonsV3.LessonStatus | LessonsV3.LessonStatusAction,
    request?: LessonsV3.LessonCancellationRequestDto | LessonsV3.LessonInvalidationRequestDto,
    studentId?: number | null,
    teacherId?: number | null
  ): Observable<LessonsV3.LFViewBase | void> {
    const isAction = Object.values(LessonsV3.LessonStatusAction).includes(status as LessonsV3.LessonStatusAction);
    if (isAction) {
      let params = appendIfDefined(new HttpParams(), 'action', status);
      params = appendIfDefined(params, 'studentId', studentId);
      params = appendIfDefined(params, 'teacherId', teacherId);
      return this.http.patch<LessonsV3.LFViewBase>(
        this.lessonsUrl(`/${lessonId}/status`),
        request ?? {},
        {params}
      );
    }
    return this.http.patch<void>(
      this.lessonsUrl(`/${lessonId}/status`),
      JSON.stringify(status),
      {headers: new HttpHeaders({'Content-Type': 'application/json'})}
    );
  }

  registerLessonFlag(
    lessonId: number,
    flag: LessonsV3.LessonFlagDto,
    studentId?: number | null,
    teacherId?: number | null
  ): Observable<LessonsV3.ApiLessonFlag> {
    let params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    params = appendIfDefined(params, 'teacherId', teacherId);
    return this.http.post<LessonsV3.ApiLessonFlag>(this.lessonsUrl(`/${lessonId}/flags`), flag, {params});
  }

  postLessonComment(
    lessonId: number,
    message: LessonsV3.LessonMessageDto
  ): Observable<LessonsV3.ApiLessonMessage> {
    return this.http.put<LessonsV3.ApiLessonMessage>(this.lessonsUrl(`/${lessonId}/comment`), message);
  }

  postLessonMessageForStudent(
    lessonId: number,
    studentId: number,
    message: string
  ): Observable<LessonsV3.ApiLessonMessage> {
    const params = appendIfDefined(new HttpParams(), 'studentId', studentId);
    const body: LessonsV3.LessonMessageDto = {lessonComment: '', privateLessonComment: '', message};
    return this.http.put<LessonsV3.ApiLessonMessage>(this.lessonsUrl(`/${lessonId}/comment`), body, {params});
  }

  updateLessonType(lessonId: number, lessonType: LessonsV3.LessonType): Observable<void> {
    return this.http.patch<void>(
      this.lessonsUrl(`/${lessonId}/type`),
      JSON.stringify(lessonType),
      {headers: new HttpHeaders({'Content-Type': 'application/json'})}
    );
  }

  updateLessonMetric(
    lessonId: number,
    metric: LessonsV3.V3LessonMetric
  ): Observable<LessonsV3.V3LessonMetric> {
    return this.http.put<LessonsV3.V3LessonMetric>(this.lessonsUrl(`/${lessonId}/metric`), metric);
  }

  // ---- student product context / credits ----

  saveStudentProductContext(
    schoolId: number,
    studentId: number,
    productCode: string,
    context: LessonsV3.ProductContextDetails
  ): Observable<void> {
    return this.http.put<void>(
      this.studentsUrl(schoolId, `/${studentId}/product-contexts/${productCode}`),
      context
    );
  }

  saveLessonBundle(
    schoolId: number,
    studentId: number,
    bundle: LessonsV3.LessonBundleDetails
  ): Observable<LessonsV3.LessonBundle> {
    return this.http.post<LessonsV3.LessonBundle>(
      this.studentsUrl(schoolId, `/${studentId}/bundles`),
      bundle
    );
  }

  updateStudentLessonBundleExpiry(
    schoolId: number,
    studentId: number,
    bundleId: number,
    expiryDate: Date
  ): Observable<void> {
    return this.http.patch<void>(
      this.studentsUrl(schoolId, `/${studentId}/bundles/${bundleId}/expiry-date`),
      JSON.stringify(expiryDate.toISOString()),
      {headers: new HttpHeaders({'Content-Type': 'application/json'})}
    );
  }

  updateStudentLessonBundleNote(
    schoolId: number,
    studentId: number,
    bundleId: number,
    note: string
  ): Observable<void> {
    return this.http.patch<void>(
      this.studentsUrl(schoolId, `/${studentId}/bundles/${bundleId}/note`),
      {note}
    );
  }

  saveStudentTechnicalProfile(
    schoolId: number,
    studentId: number,
    profile: LessonsV3.PersonalSettings
  ): Observable<LessonsV3.PersonalSettings> {
    return this.getStudentPerson(schoolId, studentId).pipe(
      switchMap((person) =>
        this.http.put<LessonsV3.PersonalSettings>(this.peopleUrl(person.id, '/col-settings'), profile)
      )
    );
  }
}
